import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { ToolbarButton, ToolbarTooltip } from '@atlaskit/editor-toolbar';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';

import type { MalleableUiPlugin, RemixDisabledReason } from '../../malleableUiPluginType';
import { messages } from '../messages';
import { getRemixDisabledTooltip } from '../remix-disabled-tooltip';

const testId = 'editor-toolbar__remix-button';

interface RemixButtonProps {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	isDisabled?: boolean;
	isRemixHidden?: boolean;
	remixDisabledReason?: RemixDisabledReason;
}

export const RemixButton = ({
	api,
	isDisabled,
	isRemixHidden,
	remixDisabledReason,
}: RemixButtonProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isRemixing, areAiEntryPointsDisabled } = useSharedPluginStateWithSelector(
		api,
		['malleableUi', 'aiExperience'],
		(states) => ({
			isRemixing:
				states.malleableUiState?.isRemixModalOpen ||
				false ||
				states.malleableUiState?.isThinking ||
				false,
			areAiEntryPointsDisabled: fg('platform_editor_ai_toggle_entry_points')
				? (states.aiExperienceState?.areAiEntryPointsDisabled ?? false)
				: false,
		}),
	);

	const effectiveDisabled = isDisabled || areAiEntryPointsDisabled;

	const handleClick = useCallback(() => {
		if (effectiveDisabled || isRemixing) {
			return;
		}
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'remixModalFromToolbar',
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'remix',
				attributes: {
					entryPoint: 'editor',
				},
			},
		}).fire();
		experienceTracker.start({
			name: ExperienceName.REMIX_START,
			id: ExperienceName.REMIX_START,
			attributes: {
				entryPoint: 'editor',
				remixSource: 'selectionToolbar',
				product: 'confluence',
			},
		});
		api?.core.actions.execute(
			api?.malleableUi.commands.openRemixModal({
				metadata: { interactionSource: 'remixModalFromToolbar' },
				source: 'selectionToolbar',
			}),
		);
	}, [api, effectiveDisabled, isRemixing, experienceTracker, createAnalyticsEvent]);

	const tooltipContent = useMemo(() => {
		if (isRemixing) {
			return formatMessage(messages.remixInProgressTooltip);
		}
		if (effectiveDisabled) {
			return getRemixDisabledTooltip(formatMessage, remixDisabledReason);
		}
		return formatMessage(messages.remixToolbarTooltip);
	}, [isRemixing, effectiveDisabled, formatMessage, remixDisabledReason]);

	if (isRemixHidden || !expValEquals('cc-maui-experiment', 'isEnabled', true)) {
		return null;
	}

	return (
		<ToolbarTooltip content={tooltipContent}>
			<ToolbarButton
				iconBefore={<AiGenerativeRemixIcon label="" size="small" />}
				testId={testId}
				onClick={handleClick}
				isDisabled={effectiveDisabled || isRemixing}
			>
				{formatMessage(messages.remixToolbarLabel)}
			</ToolbarButton>
		</ToolbarTooltip>
	);
};
